import { Box, Button, Flex, Typography } from "@jewlr/storybook/core"
import { useInfiniteQuery } from "@tanstack/react-query"
import PropTypes from "prop-types"
import queryString from "query-string"
import { useCallback, useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { Link, useLocation } from "react-router-dom"
import styled from "styled-components"

import api from "apis"
import { history } from "app-store-v2"

import ArticleCard from "./article-card"
import ArticleCardLoading from "./article-card-loading"
import Breadcrumb from "./breadcrumb"

const ArticleContainer = styled(Box)`
  display: grid;
  ${(props) => props.theme.mediaQueries.tablet`
    grid-gap: 16px;
    grid-template-columns: 1fr 1fr 1fr;
  `}
  ${(props) => props.theme.mediaQueries.desktop`
    grid-gap: 24px;
  `}
`

const LoadMore = styled(Button).attrs((props) => ({
  bg: "primary",
  color: "white",
  fontSize: "18px",
  forwardedAs: Link,
  height: "48px",
  lineHeight: "18px",
  width: "260px",
  ...props,
}))``

const Homepage = ({ articleData }) => {
  const { search } = useLocation()
  const queryParams = search ? queryString.parse(search) : {}

  const queryFn = useCallback(
    ({ pageParam = 1 }) => api.get(`/blog.json?page=${pageParam}`),
    []
  )

  const ssrData = typeof window === "undefined" ? articleData : undefined

  const {
    data,
    isLoading,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery({
    getNextPageParam: (lastPage) =>
      lastPage?.page?.current_page < lastPage?.page?.total_page_count
        ? lastPage?.page?.current_page + 1
        : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage?.page?.current_page > 1
        ? firstPage?.page?.current_page - 1
        : undefined,
    keepPreviousData: true,
    queryFn,
    queryKey: ["blogs"],
    select: (data) => {
      const { pageParams, pages } = data

      return {
        articles: (pages || []).reduce(
          (accu, curr) => [...accu, ...(curr?.articles || [])],
          []
        ),
        page: pages[pages.length - 1].page,
        pageParams,
      }
    },
    ...(ssrData?.articles && {
      initialData: {
        pageParams: ssrData?.page?.current_page
          ? [ssrData.page.current_page]
          : undefined,
        pages: [ssrData],
      },
      initialPageParam: ssrData?.page?.current_page,
    }),
  })

  const articles = data?.articles || []
  const featuredArticles = articles.filter((article) => article.featured)
  const blogArticles = articles.filter((article) => !article.featured)

  const placeholders = hasPreviousPage
    ? Math.min(
        data?.page?.current_page * data?.page?.results_per_page,
        data?.page?.total_article_count
      ) -
      data?.articles?.length -
      1
    : 0

  useEffect(() => {
    // When SSR loading a page > 1, we only have the data from the current page.
    // Here, we gradually fetch each previous page back to page 1.
    if (hasPreviousPage && !isFetchingPreviousPage) {
      fetchPreviousPage()
    }
  }, [fetchPreviousPage, isFetchingPreviousPage, hasPreviousPage])

  useEffect(() => {
    // Sync page query parameter
    if (
      data?.page?.current_page > 1 &&
      queryParams.page !== data?.page?.current_page?.toString()
    ) {
      const updatedParams = {
        ...queryParams,
        page: data?.page?.current_page,
      }
      history.replace({ search: queryString.stringify(updatedParams) })
    }
  }, [queryParams, data])

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable react/jsx-sort-props, react/no-unknown-property */}
        <title>{sl.config.site_title} Blog</title>
        <meta content={`${sl.config.site_title} Blog`} property="og:title" />
        <meta
          content={`https://${sl.config.site_url}/blog`}
          property="og:url"
        />
        {featuredArticles.length > 0 && featuredArticles[0].cover_image && (
          <meta
            content={featuredArticles[0].cover_image.fallback}
            name="og_image"
            property="og:image"
          />
        )}
        {featuredArticles.length > 0 &&
          featuredArticles.map(
            (article) =>
              article.cover_image?.srcSet?.length > 0 &&
              article.cover_image.srcSet.map((srcset) => (
                <link
                  key={`article-homepage-featured-image-${article.url_path}-${srcset.media}`}
                  rel="preload"
                  fetchPriority="high"
                  as="image"
                  imageSrcSet={srcset.srcSet}
                  imageSizes={srcset.sizes}
                  media={srcset.media}
                />
              ))
          )}
        {/* eslint-enable react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      <Breadcrumb />
      <Typography
        as="h1"
        fontSize="32px"
        fontWeight="700"
        lineHeight="36px"
        mb={5}
      >
        {sl.config.site_title} Blog
      </Typography>
      {isLoading || hasPreviousPage ? (
        <ArticleCardLoading featured />
      ) : (
        featuredArticles.map((featured) => (
          <ArticleCard article={featured} featured key={featured.url_path} />
        ))
      )}
      <ArticleContainer>
        {placeholders > 0 &&
          [...Array(placeholders)].map((_, i) => (
            <ArticleCardLoading key={`article-loading-${i}`} />
          ))}
        {blogArticles.map((blogArticle) => (
          <ArticleCard article={blogArticle} key={blogArticle.url_path} />
        ))}
        {isLoading &&
          [...Array(6)].map((_, i) => (
            <ArticleCardLoading key={`article-loading-${i}`} />
          ))}
      </ArticleContainer>
      {hasNextPage && (
        <Flex justifyContent="center" pb={{ _: 0, tablet: 5 }} pt={1} px="20px">
          <LoadMore
            disabled={isFetchingNextPage}
            loading={isFetchingNextPage}
            onClick={(e) => {
              e.preventDefault()
              fetchNextPage()
            }}
            replace
            to={`/blog?page=${data?.page?.current_page + 1}`}
          >
            {isFetchingNextPage ? "Loading" : "View More"}
          </LoadMore>
        </Flex>
      )}
    </>
  )
}

Homepage.propTypes = {
  articleData: PropTypes.object,
}

export default Homepage
